/**
 * `/my-page` の search param パーサ。
 *
 * Slack DM のアンケート通知が付ける deep link を受け取り、未回答アンケートのモーダルを
 * 自動オープンするために使う。アンケートは 2 種あり、param も分ける:
 *  - `?surveyChild=<childIssueKey>` … デザイナー貢献度アンケート（子チケット単位）
 *  - `?dleSurvey=<parentIssueKey>`  … DLE 評価アンケート（母票単位）
 *
 * 1 つの param を共用しない。両者はキーの単位（子票 / 母票）も開く view（デザイン / 開発）も
 * 違うため、同じ param だと「どちらのアンケートのリンクか」を値から判別できない。
 *
 * あわせてタブの直リンク `?tab=<tabId>` を受け取る。タブ ID は画面側の定義と同じ値で、
 * 知らない ID は落とす（ロールごとに出るタブが違うので、その人に出ないタブの扱いは画面側で決める）。
 * 先頭 `-` の付いたファイルは TanStack Router のルート生成対象外。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business /my-page の search param（デザイナー貢献度 / DLE 評価アンケートの deep link）の検証・正規化
 * @graph-connects none
 */

/** Backlog 課題キーの形（`AIRCLOSET-138536`）。 @graph-connects none */
const ISSUE_KEY_REGEX = /^[A-Z][A-Z0-9_]*-\d+$/;

/** マイページのタブ ID。 @graph-connects none */
export const MY_PAGE_TAB_IDS = [
  'board',
  'kpi',
  'oneOnOne',
  'dleEval',
  'skill',
  'feedback',
  'bug',
  'settings',
] as const;

/** マイページのタブ ID の型。 @graph-connects none */
export type MyPageTabId = (typeof MY_PAGE_TAB_IDS)[number];

/** `/my-page` の search param。 @graph-connects none */
export interface MyPageSearch {
  /**
   * デザイナー貢献度アンケートの回答対象の子チケットキー。
   * 課題キーの形に合わない値は落とす（任意文字列をそのまま画面状態へ流さない）。
   */
  surveyChild?: string;
  /**
   * DLE 評価アンケートの回答対象の母票キー。
   * 同じく課題キーの形に合わない値は落とす。
   */
  dleSurvey?: string;
  /** 開くタブ。タブ ID に合わない値は落とす。 */
  tab?: MyPageTabId;
}

/**
 * 課題キーの形なら正規化して返す。合わなければ undefined。
 * @graph-connects none
 */
function parseIssueKey(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim().toUpperCase();
  return ISSUE_KEY_REGEX.test(trimmed) ? trimmed : undefined;
}

/**
 * search param を検証して正規化する。
 *
 * 不正値はエラーにせず落とす。DM のリンクが古くて対象が既に回答済みでも
 * MyPage 自体は開けないと困るため、ここは寛容側に倒す。
 * @graph-connects parseIssueKey [calls] 課題キー形式の検証
 */
export function parseMyPageSearch(search: Record<string, unknown>): MyPageSearch {
  const result: MyPageSearch = {};
  const surveyChild = parseIssueKey(search.surveyChild);
  if (surveyChild !== undefined) result.surveyChild = surveyChild;
  const dleSurvey = parseIssueKey(search.dleSurvey);
  if (dleSurvey !== undefined) result.dleSurvey = dleSurvey;
  const tab = MY_PAGE_TAB_IDS.find((id) => id === search.tab);
  if (tab !== undefined) result.tab = tab;
  return result;
}
